/**
 * B1 -- what a ceremony's signers actually said.
 *
 * Twelve of thirteen panels take a reason and store it in its own
 * column, and no record page rendered any of them: an admission
 * challenged by one person and signed by another showed "Challenged by
 * Rashid Reviewer" with neither his words nor hers.
 *
 * That is worse than never offering the box. The rule the columns were
 * added under is that a signer who believes they recorded a
 * reservation must find it on the record; a stored comment nobody
 * shows breaks exactly that promise, and it is the one item keeping
 * the CRO's verdict from an unqualified yes.
 *
 * Shared, because four record pages need the same thing and four
 * renderings of a signature history would disagree about what a
 * signature is.
 */
import { useTranslation } from "react-i18next";

import { Bdi } from "shared/components/Bdi";
import { formatDateTime } from "shared/time/formatDate";

export interface SignatureEntry {
  /** The step: challenge, CRO sign-off, business owner sign-off... */
  roleLabel: string;
  name: string | null;
  at: string | null;
  note: string | null;
  /**
   * Round 10 F2 -- did this leg actually offer somewhere to write one?
   *
   * "Signed without a comment" is a statement about the SIGNER: they
   * were asked and declined. On most sign legs no box is offered at
   * all -- the column exists and the form never sends one -- so the
   * line read as three people refusing to explain themselves when
   * nobody had asked. Where no box was offered the absence is silent,
   * because the honest thing to say about it is nothing.
   *
   * Defaults true, so a leg that does offer one keeps saying so
   * without every caller having to opt in.
   */
  commentOffered?: boolean;
}

export function SignatureHistory({
  entries,
  testid = "signature-history",
}: {
  entries: SignatureEntry[];
  testid?: string;
}) {
  const { t, i18n } = useTranslation();
  // Only steps that have actually happened. A ceremony half way
  // through is a real state, and listing its unsigned legs as empty
  // rows would read as a record with blanks in it.
  const given = entries.filter((e) => e.at || e.name);
  if (given.length === 0) return null;
  return (
    <section data-testid={testid} className="space-y-1">
      <h3 className="font-mono text-[10px] uppercase tracking-wide text-slate-500">
        {t("signatures.heading")}
      </h3>
      <ul className="space-y-1">
        {given.map((e) => (
          <li
            key={e.roleLabel}
            data-testid={`${testid}-${e.roleLabel}`}
            className="rounded border border-paper-2 px-2 py-1 text-xs"
          >
            <p className="text-slate-700">
              <span className="font-medium">{e.roleLabel}</span>
              {e.name ? (
                <>
                  {" "}
                  <Bdi>{e.name}</Bdi>
                </>
              ) : null}
              {/* Round 12 H5 -- "CRO Sara Al-Hassan18 Sept 2026, 11:32",
                  on EVERY signature row in the product, because this
                  component draws them all. A margin separated the
                  signer from the moment on screen and nothing separated
                  them in the text, which is what a copy into a board
                  pack, a screen reader and an export all get -- of a
                  ceremony record, where who signed and when are the two
                  facts the row exists to carry. */}
              {e.at ? (
                <>
                  {" \u00b7 "}
                  <span className="ms-2 text-slate-500">
                    {formatDateTime(e.at, i18n.language)}
                  </span>
                </>
              ) : null}
            </p>
            {/* The words, which is the whole point. An absence is
                stated rather than left blank: "signed without comment"
                and "the comment did not load" must not look alike. */}
            {e.note ? (
              <p
                data-testid={`${testid}-note-${e.roleLabel}`}
                className="mt-0.5 text-slate-800"
                dir="auto"
              >
                {e.note}
              </p>
            ) : e.commentOffered === false ? null : (
              <p className="mt-0.5 italic text-slate-400">
                {t("signatures.noComment")}
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
